import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AbsenceCategory } from "@myapp/contract";
import {
  ABSENCE_CATEGORIES,
  getAbsenceCategoryLabel,
} from "../../../../entities/absence";
import { routes } from "../../../../shared/config/routes";
import { useOutsideClick } from "../../../../shared/hooks/useOutsideClick";
import { useCanPlanAllAbsences } from "../../../../shared/hooks/useUserRoles";
import { Button } from "../../../../shared/ui/controls/Button/Button";

/** Trigger and menu have to line up, so the width is stated once. */
const WIDTH_CLASS = "w-[260px]";

const createAbsenceHref = (category: AbsenceCategory): string =>
  `${routes.zeitmanagement.create}?category=${category}`;

export const CategoryActionMenu: React.FC = () => {
  const navigate = useNavigate();
  const canPlanAnyCategory = useCanPlanAllAbsences();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(containerRef, close, open);

  const openCategory = (category: AbsenceCategory) => {
    close();
    navigate(createAbsenceHref(category));
  };

  if (!canPlanAnyCategory) {
    return (
      <Button
        variant="surface"
        className={WIDTH_CLASS}
        onClick={() => openCategory("PERSONAL_LEAVE")}
      >
        {getAbsenceCategoryLabel("PERSONAL_LEAVE")}
      </Button>
    );
  }

  return (
    <div
      className="relative"
      ref={containerRef}
      onKeyDown={(event) => {
        if (event.key === "Escape") close();
      }}
    >
      <Button
        variant="surface"
        className={WIDTH_CLASS}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Abwesenheit planen
      </Button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 top-full z-50 mt-1 ${WIDTH_CLASS} rounded-lg bg-white shadow-lg`}
        >
          {ABSENCE_CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              role="menuitem"
              className="w-full px-4 py-3 text-left font-['PT_Sans'] text-[16px] text-[#333] hover:bg-[#F5F5F5] first:rounded-t-lg last:rounded-b-lg"
              onClick={() => openCategory(category)}
            >
              {getAbsenceCategoryLabel(category)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
